import { useEffect, useId, useState, type CSSProperties, type KeyboardEvent, type ReactNode } from "react";

export type FundamentalStory = {
  id: string;
  number: string;
  label: string;
  eyebrow: string;
  title: string;
  body: string;
  detail: string;
  accent: string;
  background: string;
  backgroundPosition: string;
};

const FUNDAMENTAL_STORIES: readonly FundamentalStory[] = [
  {
    id: "connect",
    number: "01",
    label: "Connect",
    eyebrow: "The relationship layer",
    title: "The people in the room change what becomes possible.",
    body: "We bring thoughtful people together in a setting where trust can form, perspectives can be shared, and relationships can continue long after the conversation ends.",
    detail: "Start with the right room—and make every introduction matter.",
    accent: "#E91E8C",
    background: "/media/speakers/harry-on-stage.webp",
    backgroundPosition: "center 30%",
  },
  {
    id: "learn",
    number: "02",
    label: "Learn",
    eyebrow: "The shared-wisdom layer",
    title: "Shared experience turns questions into clearer next steps.",
    body: "We make room for candid exchange, practical insight, and the honest questions that help people see a challenge from a more useful angle.",
    detail: "Leave every conversation with a sharper perspective you can use.",
    accent: "#F5A623",
    background: "/media/backgrounds/event-detail-community.webp",
    backgroundPosition: "center 42%",
  },
  {
    id: "grow",
    number: "03",
    label: "Grow",
    eyebrow: "The momentum layer",
    title: "When people rise together, momentum has more purpose.",
    body: "We encourage personal development, stronger leadership, and professional progress that is rooted in contribution—not simply individual achievement.",
    detail: "Turn connection and insight into forward motion for the whole community.",
    accent: "#4AB857",
    background: "/media/backgrounds/home-hero-community.webp",
    backgroundPosition: "center 46%",
  },
];

/**
 * How long the outgoing copy stays mounted, in milliseconds.
 *
 * Must outlast the exit keyframes plus the incoming delay, or the old text is
 * unmounted mid-throw and the swap reads as a cut. Kept beside the CSS below,
 * since the two numbers only make sense together.
 */
const STORY_SWAP_MS = 560;

/**
 * How long each story holds before the next one is thrown in, in milliseconds.
 *
 * Eight seconds. The body copy runs about thirty words, which is roughly nine
 * seconds of reading at an unhurried pace, and the detail line adds a few more —
 * but a carousel that waits for the slowest reader feels stalled, and anyone who
 * wants longer can hover, focus, or click a tab to stop it. Shorter than about six
 * and the paragraph is gone before it has been read; longer than about ten and
 * most visitors never learn the section moves at all.
 */
const STORY_DWELL_MS = 8000;

/** The story copy, rendered twice during a swap: once leaving, once arriving. */
function StoryBody({ story }: { story: FundamentalStory }) {
  return (
    <>
      <p className="home-fundamentals__eyebrow">{story.eyebrow}</p>
      <h3>{story.title}</h3>
      <p className="home-fundamentals__body">{story.body}</p>
      <div className="home-fundamentals__detail">
        <span>CHI signal</span>
        <strong>{story.detail}</strong>
      </div>
    </>
  );
}

type HomeFundamentalsExperienceProps = {
  /** Stories to rotate through. Defaults to the home page's CHI fundamentals. */
  stories?: readonly FundamentalStory[];
  /** Small line above the heading. */
  introKicker?: string;
  /** The section heading. A node, so a page can accent part of it as home does. */
  introHeading?: ReactNode;
  /** Accessible name for the section and its tablist. */
  ariaLabel?: string;
};

export default function HomeFundamentalsExperience({
  stories = FUNDAMENTAL_STORIES,
  introKicker = "How we make room for progress",
  introHeading = <>Connect. Learn. <span>Grow.</span></>,
  ariaLabel = "The CHI Alliance fundamentals",
}: HomeFundamentalsExperienceProps = {}) {
  const [activeIndex, setActiveIndex] = useState(0);
  /**
   * The story on its way out, plus which way the swap is travelling. Null when
   * nothing is in flight, which is why the first render animates in from the
   * default direction rather than throwing anything away.
   */
  const [outgoing, setOutgoing] = useState<{ story: FundamentalStory; direction: 1 | -1 } | null>(null);
  /**
   * Bumped on every advance so the progress line's keyframes restart. Re-keying an
   * element is the reliable way to replay a CSS animation; toggling the class alone
   * is subject to the browser batching both changes into one style recalculation and
   * never seeing an interruption.
   */
  const [tick, setTick] = useState(0);
  const id = useId().replace(/:/g, "");
  const activeStory = stories[activeIndex] ?? stories[0];
  const direction = outgoing?.direction ?? 1;

  const selectStory = (index: number) => {
    if (index === activeIndex) return;
    // Direction of travel, so the copy leaves and arrives along the same arc
    // rather than always swinging the same way regardless of which tab was hit.
    setOutgoing({ story: activeStory, direction: index > activeIndex ? 1 : -1 });
    setActiveIndex(index);
    setTick(current => current + 1);
  };

  /*
   * The auto-advance.
   *
   * Runs on one unbroken cadence: hovering the section or focusing inside it does
   * not hold, slow, or restart it. An earlier version paused on hover, which meant
   * the rotation stalled for anyone whose pointer happened to rest over the banner
   * while they scrolled past, and the countdown line jumped back to empty when they
   * moved away. A steady interval is more predictable than one that reacts to the
   * pointer.
   *
   * Skipped entirely when the visitor asks for reduced motion. Content that moves on
   * its own is what that setting is about, and the boomerang is already disabled
   * there — leaving the timer running would swap the copy with no transition at all,
   * which is more disorienting than not moving.
   *
   * Depending on `tick` as well as `activeIndex` is what makes choosing a tab by hand
   * restart the dwell. That is deliberate and separate from hover: a story selected
   * deliberately should get its full turn rather than whatever remained of the
   * previous one's.
   */
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const timer = window.setTimeout(() => {
      const next = (activeIndex + 1) % stories.length;
      setOutgoing({ story: stories[activeIndex], direction: 1 });
      setActiveIndex(next);
      setTick(current => current + 1);
    }, STORY_DWELL_MS);

    return () => window.clearTimeout(timer);
  }, [activeIndex, tick, stories]);

  /*
   * Unmounts the outgoing copy once its keyframes have finished. Re-running on
   * every new outgoing value means a fast run through the tabs restarts the clock
   * instead of clearing a swap that is still in flight.
   */
  useEffect(() => {
    if (!outgoing) return;
    const timer = window.setTimeout(() => setOutgoing(null), STORY_SWAP_MS);
    return () => window.clearTimeout(timer);
  }, [outgoing]);
  const focusStory = (index: number) => {
    window.requestAnimationFrame(() => document.getElementById(`${id}-fundamental-tab-${index}`)?.focus());
  };

  const handleTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (!["ArrowRight", "ArrowLeft", "ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
    event.preventDefault();
    const forward = event.key === "ArrowRight" || event.key === "ArrowDown";
    const nextIndex = event.key === "Home"
      ? 0
      : event.key === "End"
        ? stories.length - 1
        : (index + (forward ? 1 : -1) + stories.length) % stories.length;
    selectStory(nextIndex);
    focusStory(nextIndex);
  };

  return (
    <section
      className="home-fundamentals"
      style={{ "--fundamental-accent": activeStory.accent } as CSSProperties}
      data-active-fundamental={activeStory.id}
      aria-label={ariaLabel}
    >
      <div
        className="home-fundamentals__image"
        aria-hidden="true"
        style={{ backgroundImage: `url('${activeStory.background}')`, backgroundPosition: activeStory.backgroundPosition }}
      />
      <div className="home-fundamentals__overlay" aria-hidden="true" />

      <div className="home-fundamentals__inner container">
        {/* The control rail stays ahead of the panel in the DOM, which is both the
            expected tablist order for assistive technology and what puts it above
            the story when the layout stacks on mobile. On desktop CSS `order` moves
            it to the right of the content, so the choices sit beside the words they
            change rather than in a separate band above them. */}
        <div
          className="home-fundamentals__nav"
          role="tablist"
          aria-orientation="vertical"
          aria-label={`Explore ${ariaLabel}`}
        >
          {stories.map((story, index) => {
            const isActive = activeIndex === index;
            return (
              <button
                id={`${id}-fundamental-tab-${index}`}
                key={story.id}
                className="home-fundamentals__tab"
                data-active={isActive || undefined}
                style={{ "--fundamental-tab-accent": story.accent } as CSSProperties}
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-controls={`${id}-fundamental-panel`}
                tabIndex={isActive ? 0 : -1}
                onClick={() => selectStory(index)}
                onKeyDown={(event) => handleTabKeyDown(event, index)}
              >
                <span className="home-fundamentals__tab-number">{story.number}</span>
                <span className="home-fundamentals__tab-label">{story.label}</span>
                <span className="home-fundamentals__tab-dot" aria-hidden="true" />
                {/*
                  Time remaining on the active story. Re-keyed on every advance so the
                  keyframes replay from empty, and only rendered for the active tab —
                  a row of idle progress lines would read as five stalled timers.
                */}
                {isActive && (
                  <span
                    key={`${activeIndex}-${tick}`}
                    className="home-fundamentals__tab-progress"
                    aria-hidden="true"
                  />
                )}
              </button>
            );
          })}
        </div>

        <article
          id={`${id}-fundamental-panel`}
          className="home-fundamentals__content"
          role="tabpanel"
          aria-live="polite"
          aria-labelledby={`${id}-fundamental-tab-${activeIndex}`}
        >
          <div className="home-fundamentals__section-intro">
            <p>{introKicker}</p>
            <h2>{introHeading}</h2>
          </div>

          {/*
            Both copies share this stage so the outgoing one can be taken out of flow
            and overlaid — otherwise two stacked blocks would double the height for
            the length of the swap and shove the layout around.
          */}
          <div
            className="home-fundamentals__stage"
            style={{ "--fundamental-direction": direction } as CSSProperties}
          >
            {outgoing && (
              /* aria-hidden: the panel is a live region, and announcing the copy
                 that is leaving alongside the copy that is arriving would read as
                 one garbled string. */
              <div
                key={`${outgoing.story.id}-outgoing`}
                className="home-fundamentals__story home-fundamentals__story--outgoing"
                aria-hidden="true"
              >
                <StoryBody story={outgoing.story} />
              </div>
            )}

            <div key={activeStory.id} className="home-fundamentals__story">
              <StoryBody story={activeStory} />
            </div>
          </div>
        </article>
      </div>

      <style>{`
        .home-fundamentals {
          position: relative;
          isolation: isolate;
          min-height: clamp(42rem, 64vw, 52rem);
          padding: clamp(3.5rem, 7vw, 6rem) 0;
          overflow: hidden;
          background: #070b18;
          border-top: 1px solid rgba(255,255,255,0.12);
          border-bottom: 1px solid rgba(255,255,255,0.12);
        }

        /*
          Keep the background full bleed, but use the same shared container edge as
          the Meet Harry section and every other Home section. The previous
          viewport-based padding put this copy 2rem farther in at desktop widths.
        */
        .home-fundamentals__inner {
          display: flex;
          align-items: center;
          justify-content: flex-start;
          gap: clamp(1.75rem, 4vw, 4rem);
          min-height: calc(clamp(42rem, 64vw, 52rem) - clamp(7rem, 14vw, 12rem));
        }

        .home-fundamentals__image,
        .home-fundamentals__overlay {
          position: absolute;
          inset: 0;
          pointer-events: none;
        }

        .home-fundamentals__image {
          z-index: -2;
          background-repeat: no-repeat;
          background-size: cover;
          filter: saturate(0.78) contrast(1.03) brightness(0.85);
          opacity: 0.94;
          transition: background-position 420ms cubic-bezier(0.23, 1, 0.32, 1), opacity 260ms cubic-bezier(0.23, 1, 0.32, 1);
        }

        .home-fundamentals__overlay {
          z-index: -1;
          background:
            radial-gradient(circle at 18% 16%, rgba(255,156,203,0.12), transparent 20%),
            radial-gradient(circle at 78% 26%, rgba(247,106,181,0.14), transparent 25%),
            radial-gradient(circle at 62% 84%, rgba(221,78,157,0.11), transparent 29%),
            radial-gradient(circle at 79% 17%, color-mix(in srgb, var(--fundamental-accent) 22%, transparent), transparent 31%),
            radial-gradient(ellipse at 18% 71%, color-mix(in srgb, var(--fundamental-accent) 16%, transparent), transparent 42%),
            linear-gradient(90deg, rgba(5,9,22,0.98) 0%, rgba(5,9,22,0.95) 26%, rgba(5,9,22,0.78) 48%, rgba(5,9,22,0.45) 67%, rgba(5,9,22,0.34) 100%),
            linear-gradient(180deg, rgba(4,8,19,0.34) 0%, rgba(4,8,19,0.08) 42%, rgba(4,8,19,0.74) 100%);
          transition: background 260ms cubic-bezier(0.23, 1, 0.32, 1);
        }

        /*
          Centred as one block rather than pushed apart with space-between. The
          intro and the story read as a single column of copy, so the vertical
          rhythm between them is set by the story's own margin below and not by
          however tall the section happens to be.
        */
        .home-fundamentals__content {
          position: relative;
          z-index: 1;
          display: flex;
          flex: 1;
          flex-direction: column;
          justify-content: center;
          min-width: 0;
          max-width: 54rem;
          text-shadow: 0 3px 18px rgba(0,0,0,0.72);
        }

        .home-fundamentals__section-intro { max-width: 30rem; }

        .home-fundamentals__section-intro > p,
        .home-fundamentals__eyebrow {
          margin: 0;
          color: var(--fundamental-accent);
          font-family: 'Space Grotesk', sans-serif;
          font-size: clamp(0.65rem, 0.78vw, 0.76rem);
          font-weight: 800;
          letter-spacing: 0.13em;
          text-transform: uppercase;
        }

        .home-fundamentals__section-intro h2 {
          margin: 0.65rem 0 0;
          color: #fff;
          font-family: 'Space Grotesk', sans-serif;
          font-size: clamp(2rem, 4vw, 3.55rem);
          font-weight: 720;
          letter-spacing: -0.065em;
          line-height: 1;
          text-shadow: 0 5px 28px rgba(0,0,0,0.8);
        }

        .home-fundamentals__section-intro h2 span {
          color: var(--fundamental-accent);
        }

        /*
          Was clamp(4.5rem, 11vw, 9rem), which at wide viewports opened a gap large
          enough that the heading and the story it introduces stopped reading as one
          thought. Close enough now to be related, still clearly a new block.
        */
        .home-fundamentals__stage {
          position: relative;
          max-width: min(38rem, 100%);
          margin-top: clamp(1.35rem, 2.6vw, 2.25rem);
        }

        .home-fundamentals__story {
          animation: home-fundamentals-story-enter 430ms cubic-bezier(0.33, 1, 0.68, 1) 90ms both;
        }

        /*
          Lifted out of flow so the arriving copy alone sets the stage height, and
          made inert so a click landing mid-swap cannot hit the text that is leaving.
        */
        .home-fundamentals__story--outgoing {
          position: absolute;
          top: 0;
          right: 0;
          left: 0;
          pointer-events: none;
          animation: home-fundamentals-story-exit 330ms cubic-bezier(0.4, 0, 0.9, 0.42) both;
        }

        .home-fundamentals__eyebrow {
          display: inline-flex;
          padding-bottom: 0.6rem;
          border-bottom: 1px solid color-mix(in srgb, var(--fundamental-accent) 72%, transparent);
        }

        .home-fundamentals__story h3 {
          max-width: 36rem;
          margin: 0.95rem 0 0;
          color: #fff;
          font-family: 'Space Grotesk', sans-serif;
          font-size: clamp(1.85rem, 3.65vw, 3.35rem);
          font-weight: 560;
          letter-spacing: -0.06em;
          line-height: 1.05;
          text-wrap: balance;
          text-shadow: 0 5px 28px rgba(0,0,0,0.82);
        }

        .home-fundamentals__body {
          max-width: 32rem;
          margin: 1.05rem 0 0;
          color: rgba(239,244,252,0.86);
          font-family: 'DM Sans', 'Inter', sans-serif;
          font-size: clamp(0.92rem, 1.18vw, 1.05rem);
          line-height: 1.66;
        }

        .home-fundamentals__detail {
          display: grid;
          gap: 0.28rem;
          max-width: 34rem;
          margin-top: 1.25rem;
          padding: 0.75rem 0 0.1rem 0.9rem;
          border-left: 2px solid var(--fundamental-accent);
        }

        .home-fundamentals__detail span {
          color: var(--fundamental-accent);
          font-family: 'Space Grotesk', sans-serif;
          font-size: 0.62rem;
          font-weight: 800;
          letter-spacing: 0.12em;
          text-transform: uppercase;
        }

        .home-fundamentals__detail strong {
          color: rgba(255,255,255,0.94);
          font-family: 'DM Sans', 'Inter', sans-serif;
          font-size: clamp(0.82rem, 1.05vw, 0.94rem);
          font-weight: 650;
          line-height: 1.42;
        }

        /*
          A rail beside the content rather than a band above it: order puts it on
          the right while the DOM keeps the tablist ahead of its panel, and
          align-self centres it against the section regardless of how tall the
          active story runs.
        */
        .home-fundamentals__nav {
          position: relative;
          z-index: 2;
          order: 2;
          align-self: center;
          flex-shrink: 0;
          display: flex;
          flex-direction: column;
          width: clamp(12.5rem, 17vw, 16rem);
          overflow: hidden;
          border: 1px solid rgba(255,255,255,0.17);
          border-radius: 1rem;
          background: rgba(4,8,19,0.66);
          backdrop-filter: blur(17px) saturate(1.08);
          -webkit-backdrop-filter: blur(17px) saturate(1.08);
          box-shadow: 0 22px 60px rgba(0,0,0,0.45);
        }

        .home-fundamentals__tab {
          position: relative;
          display: flex;
          align-items: center;
          gap: 0.7rem;
          min-height: 5.4rem;
          padding: 1rem 2.3rem 1rem 1.45rem;
          border: 0;
          border-bottom: 1px solid rgba(255,255,255,0.14);
          background: transparent;
          color: rgba(255,255,255,0.54);
          cursor: pointer;
          font-family: 'Space Grotesk', sans-serif;
          text-align: left;
          transition: color 180ms cubic-bezier(0.23, 1, 0.32, 1), background-color 180ms cubic-bezier(0.23, 1, 0.32, 1), transform 180ms cubic-bezier(0.23, 1, 0.32, 1);
        }

        .home-fundamentals__tab:last-child { border-bottom: 0; }

        /* The active marker runs down the leading edge now that the tabs stack. */
        .home-fundamentals__tab::before {
          position: absolute;
          top: 0;
          bottom: 0;
          left: 0;
          width: 2px;
          background: transparent;
          content: '';
          transition: background-color 180ms cubic-bezier(0.23, 1, 0.32, 1);
        }

        .home-fundamentals__tab:hover {
          color: rgba(255,255,255,0.88);
          background: color-mix(in srgb, var(--fundamental-tab-accent) 10%, transparent);
        }

        .home-fundamentals__tab:active { transform: scale(0.98); }

        .home-fundamentals__tab[data-active] {
          color: #fff;
          background: color-mix(in srgb, var(--fundamental-tab-accent) 17%, rgba(4,8,19,0.42));
        }

        .home-fundamentals__tab[data-active]::before { background: var(--fundamental-tab-accent); }

        .home-fundamentals__tab:focus-visible {
          z-index: 1;
          outline: 3px solid rgba(255,255,255,0.96);
          outline-offset: -4px;
        }

        .home-fundamentals__tab-number {
          color: var(--fundamental-tab-accent);
          font-size: clamp(0.68rem, 1vw, 0.84rem);
          font-weight: 800;
          letter-spacing: 0.09em;
        }

        .home-fundamentals__tab-label {
          font-size: clamp(0.92rem, 1.22vw, 1.08rem);
          font-weight: 700;
          letter-spacing: -0.022em;
        }

        /* Trailing edge of the row, vertically centred, now that there is no
           bottom strip beneath the label to sit in. */
        .home-fundamentals__tab-dot {
          position: absolute;
          top: 50%;
          right: 1.1rem;
          margin-top: -0.21rem;
          width: 0.42rem;
          height: 0.42rem;
          border-radius: 999px;
          background: var(--fundamental-tab-accent);
          box-shadow: 0 0 12px var(--fundamental-tab-accent);
          opacity: 0;
          transform: scale(0.55);
          transition: opacity 180ms cubic-bezier(0.23, 1, 0.32, 1), transform 180ms cubic-bezier(0.23, 1, 0.32, 1);
        }

        .home-fundamentals__tab[data-active] .home-fundamentals__tab-dot { opacity: 1; transform: scale(1); }

        /*
          The dwell timer, drawn along the bottom edge of the active tab. Animated
          with scaleX from a left origin rather than width, so it runs on the
          compositor and cannot cause layout work eight seconds at a time.

          The duration here is STORY_DWELL_MS. If one changes the other has to.
        */
        .home-fundamentals__tab-progress {
          position: absolute;
          right: 0;
          bottom: 0;
          left: 0;
          height: 2px;
          border-radius: 999px;
          background: var(--fundamental-tab-accent);
          opacity: 0.85;
          transform: scaleX(0);
          transform-origin: left center;
          animation: home-fundamentals-progress 8000ms linear forwards;
        }

        /* No pause state: the line runs the full dwell every time, so what it shows
           is always the actual time left before the next story arrives. */

        @keyframes home-fundamentals-progress {
          from { transform: scaleX(0); }
          to { transform: scaleX(1); }
        }

        /*
          The boomerang. --fundamental-direction is +1 or -1 depending on which way
          the tabs were travelled, so the throw and the return share one arc instead
          of the copy always swinging the same way.

          The outgoing text winds up a touch the wrong way before it leaves, which is
          what makes it read as thrown rather than slid. The incoming text overshoots
          its resting place and settles back — the return catch.
        */
        @keyframes home-fundamentals-story-exit {
          0% { opacity: 1; transform: none; }
          20% {
            opacity: 0.92;
            transform: translateX(calc(var(--fundamental-direction, 1) * 0.5rem)) rotate(calc(var(--fundamental-direction, 1) * 0.5deg));
          }
          100% {
            opacity: 0;
            transform: translateX(calc(var(--fundamental-direction, 1) * -3.5rem)) rotate(calc(var(--fundamental-direction, 1) * -2.6deg)) scale(0.955);
          }
        }

        @keyframes home-fundamentals-story-enter {
          0% {
            opacity: 0;
            transform: translateX(calc(var(--fundamental-direction, 1) * 3.3rem)) rotate(calc(var(--fundamental-direction, 1) * 2.4deg)) scale(0.968);
          }
          58% {
            opacity: 1;
            transform: translateX(calc(var(--fundamental-direction, 1) * -0.5rem)) rotate(calc(var(--fundamental-direction, 1) * -0.4deg)) scale(1.006);
          }
          80% {
            transform: translateX(calc(var(--fundamental-direction, 1) * 0.14rem)) rotate(0deg) scale(1);
          }
          100% { opacity: 1; transform: none; }
        }

        @media (max-width: 1080px) {
          /* Too narrow for copy and a rail side by side: the rail returns to a
             horizontal band, and the DOM order that put it first takes over again. */
          .home-fundamentals__inner {
            flex-direction: column;
            align-items: stretch;
            gap: clamp(1.75rem, 4vw, 2.75rem);
          }
          .home-fundamentals__nav {
            order: 0;
            align-self: stretch;
            flex-direction: row;
            width: 100%;
          }
          .home-fundamentals__tab {
            flex: 1;
            justify-content: center;
            min-height: 5.6rem;
            padding: 1rem 1.1rem;
            border-bottom: 0;
            border-right: 1px solid rgba(255,255,255,0.14);
            text-align: center;
          }
          .home-fundamentals__tab:last-child { border-right: 0; }
          /* Active marker moves to the top edge here, because the bottom edge is
             where the dwell line lives and two 2px rules stacked on the same edge
             read as one thicker rule of indeterminate meaning. */
          .home-fundamentals__tab::before {
            top: 0;
            right: 0;
            bottom: auto;
            left: 0;
            width: auto;
            height: 2px;
          }
          .home-fundamentals__tab-dot { top: auto; right: auto; bottom: 0.5rem; margin-top: 0; }
          .home-fundamentals__content { max-width: none; }
        }

        @media (max-width: 760px) {
          .home-fundamentals { min-height: 0; padding: 0; }
          .home-fundamentals__inner { display: block; min-height: 0; }
          .home-fundamentals__image { background-position: 66% center !important; opacity: 0.82; }
          .home-fundamentals__overlay {
            background:
              radial-gradient(circle at 16% 14%, rgba(255,156,203,0.1), transparent 24%),
              radial-gradient(circle at 84% 58%, rgba(247,106,181,0.12), transparent 27%),
              radial-gradient(circle at 76% 12%, color-mix(in srgb, var(--fundamental-accent) 18%, transparent), transparent 32%),
              linear-gradient(180deg, rgba(5,9,22,0.48) 0%, rgba(5,9,22,0.77) 36%, #070b18 77%, #070b18 100%);
          }
          .home-fundamentals__content {
            width: 100%;
            min-height: 42rem;
            padding: 3rem 0 3.2rem;
          }
          .home-fundamentals__section-intro h2 { font-size: clamp(1.9rem, 9vw, 2.85rem); }
          .home-fundamentals__story { max-width: 100%; margin-top: 1.6rem; }
          .home-fundamentals__story h3 { font-size: clamp(1.55rem, 7.8vw, 2.3rem); }
          /* Full-bleed band again at this width, so the rail's card treatment goes. */
          .home-fundamentals__nav {
            position: relative;
            min-height: 5.1rem;
            border-width: 0 0 1px;
            border-radius: 0;
            box-shadow: none;
          }
          .home-fundamentals__tab { flex-direction: column; gap: 0.18rem; min-height: 5.1rem; padding: 0.68rem 0.25rem 1.05rem; }
          .home-fundamentals__tab-number { font-size: 0.63rem; }
          .home-fundamentals__tab-label { font-size: 0.82rem; }
          .home-fundamentals__tab-dot { bottom: 0.38rem; }
        }

        @media (prefers-reduced-motion: reduce) {
          .home-fundamentals__image,
          .home-fundamentals__overlay,
          .home-fundamentals__tab,
          .home-fundamentals__tab::before,
          .home-fundamentals__tab-dot { transition: none; }
          .home-fundamentals__story { animation: none; }
          /* A thrown-and-caught arc is exactly the motion this setting asks us to
             drop, so the old copy is not shown leaving at all — the new text is
             simply there. */
          .home-fundamentals__story--outgoing { display: none; }
          /* The rotation does not run under this setting, so a countdown to a swap
             that will never come would be a lie. The component skips the timer; this
             removes the line that reported on it. */
          .home-fundamentals__tab-progress { display: none; }
        }
      `}</style>
    </section>
  );
}
